<template>
  <div style="background-color: #f4f0e3" class="footer-offset">

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-linear v-if="loading" indeterminate color="amber"></v-progress-linear>
      <p class="mt-4 barlow-regular grey--text text--darken-3 font-size-md">Loading tour information...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="d-flex justify-center py-8 width-100">
      <v-card class="width-80-vw pa-6" color="grey lighten-4" rounded="xl">
        <div class="d-flex flex-row align-center renedian-dark-red--text text-uppercase jost-bold font-size-md"><v-icon color="renedian-dark-red" class="font-size-40 mr-4">mdi-alert-circle</v-icon> Sorry, something went wrong</div>
        <div class="barlow-regular grey--text text--darken-3 font-size-sm line-height-5">{{ error }}some error</div>
        <div class="barlow-regular grey--text text--darken-3 font-size-xs line-height-5 mt-4">Please refresh and try again. If the problem persists please contact us.</div>
      </v-card>
    </div>

    <!-- Tour Content -->
    <div v-else-if="template" class="tour-content">
      
      <RippedHeader :breadcrumb="'Home / Tours / ' + template.location.region.name + ' / ' + template.name" :title="template.name" :carousel="template?.images?.carousel" :badgeImage="template?.images?.badge" />

      <!-- Tour Description -->
      <div class="my-12 text-center tour-description-block">
        <div class="barlow-condensed-bold-italic renedian-forest-green--text text-uppercase mb-4 font-size-xl">{{ template.content.website.description }}</div>
        <div class="barlow-regular grey--text text--darken-3 font-size-sm line-height-5">{{ template.content.website.detailedDescription }}</div>
      </div>

      <!-- Tour Summary -->
      <div class="py-8 d-flex flex-column flex-md-row align-self-center justify-center" style="padding: 0 5vw;">

        <div class="d-flex flex-row flex-md-1 align-center">
          <img v-if="template.location.countries.length == 1" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F1_Countries_icon.svg?alt=media&token=cd97fe6a-522c-4654-ad0b-7a1f41a1dff3" alt="" />
          <img v-if="template.location.countries.length == 2" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F2_Countries_icon.svg?alt=media&token=a74853e4-9c83-4313-a267-4ee17f20a16c" alt="" />
          <img v-if="template.location.countries.length == 3" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F3_Countries_icon.svg?alt=media&token=31dd47bd-d8a6-4b0b-b61e-1cd9e24d9eec" alt="" />
          <img v-if="template.location.countries.length == 4" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F4_Countries_icon.svg?alt=media&token=2373571c-56a7-44bc-92c6-15f13d00f46d" alt="" />
          <img v-if="template.location.countries.length == 5" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F5_Countries_icon.svg?alt=media&token=8ab626fc-2a02-491d-984a-4f57ebf699cd" alt="" />
          <img v-if="template.location.countries.length == 6" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F6_Countries_icon.svg?alt=media&token=26df114e-a352-4eeb-8461-a9fa71fbdc2b" alt="" />
          <img v-if="template.location.countries.length == 7" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F7_Countries_icon.svg?alt=media&token=9c2bf405-f341-4f4d-b0ff-230d8adac88b" alt="" />
          <img v-if="template.location.countries.length == 8" class="passport-icon" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Ftour%2F8_Countries_icon.svg?alt=media&token=ea75db82-e6aa-4d37-b795-c97645624c88" alt="" />
          <div class="d-flex flex-column renedian-dark-red--text ml-4 barlow-condensed-semibold font-size-sm">
            <div class="barlow-condensed-bold-italic text-uppercase">Countries visited on this tour:</div>
            <div class="barlow-condensed-regular-italic">{{ template.location.countries.join(', ') }}</div>
          </div>
        </div>
        <div class="flex-1 mt-md-0">
          <RidingBar :riding="template.riding" :duration="template.duration" />
        </div>
      </div>

      <!-- Tour Menu -->
      <div class="d-flex flex-column flex-md-row width-100 renedian-burnt-orange--background mt-8">
        <v-btn text large class="flex-1 text-center text-uppercase barlow-bold font-size-sm white--text tour-menu-button" @click="scrollToSection('highlights')">highlights</v-btn>
        <v-divider class="d-block d-md-none"></v-divider>
        <v-btn text large class="flex-1 text-center text-uppercase barlow-bold font-size-sm white--text tour-menu-button" @click="scrollToSection('itinerary')">itinerary</v-btn>
        <v-divider class="d-block d-md-none"></v-divider>
        <v-btn text large class="flex-1 text-center text-uppercase barlow-bold font-size-sm white--text tour-menu-button" @click="scrollToSection('prices')">prices & dates</v-btn>
        <v-btn text large class="flex-1 d-none d-lg-flex align-center text-center text-uppercase barlow-bold font-size-sm white--text tour-menu-button" @click="scrollToSection('rider-stories')">rider stories</v-btn>
        <v-btn text large class="flex-1 d-none d-lg-flex align-center text-center text-uppercase barlow-bold font-size-sm white--text tour-menu-button" @click="scrollToSection('local-lore')">local lore</v-btn>
      </div>
      <div class="d-flex flex-column d-md-flex flex-md-row d-lg-none width-100 renedian-sand--background">
        <v-btn text large class="flex-1 text-center text-uppercase barlow-bold font-size-sm renedian-very-dark-red--text tour-menu-button" @click="scrollToSection('rider-stories')">rider stories</v-btn>
        <v-divider class="d-block d-md-none"></v-divider>
        <v-btn text large class="flex-1 text-center text-uppercase barlow-bold font-size-sm renedian-very-dark-red--text tour-menu-button" @click="scrollToSection('local-lore')">local lore</v-btn>
      </div>

      <!-- Tour Highlights -->
      <div ref="highlights" id="highlights" class="d-flex flex-column align-center mt-12">
        <div class="d-flex jost-bold grey--text text--darken-3 text-uppercase text-center font-size-xl">Highlights</div>
        <div class="width-95-vw mt-3 mt-md-6 ">
          <div class="renedian-sand--background pa-6 pa-md-12" style="border-radius: 15px;">
            <div v-if="hasHighlights" class="d-flex flex-column flex-md-row">
              <div class="flex-1 pr-md-8 pl-md-12">
                <div class="barlow-condensed-semibold renedian-dark-red--text text-uppercase mt-0 mt-md-12 font-size-lg">{{ currentHighlight.title }}</div>
                <v-select
                  v-model="selectedHighlightId"
                  solo
                  class="mt-4 d-none d-md-block"
                  :items="highlights"
                  item-text="title"
                  item-value="id"
                  hide-details
                  :menu-props="{ offsetY: true }"
                />

                <div class="d-none d-md-block barlow-regular mt-4 font-size-sm line-height-5">{{ currentHighlight.description }}</div>

                <div class="d-none d-md-flex flex-row align-center justify-center mt-12">
                  <img @click="prevHighlight" style="width: 2vw;" class="cursor-pointer" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FArrow%20-%20Left.svg?alt=media&token=343e71c9-b83d-4f04-907a-4a5f9f391fec" alt="" />
                  <v-icon @click="toggleAutoRotate" class="cursor-pointer mx-12" :style="{ fontSize: '2.5vw', color: '#CA6837' }">
                    {{ isAutoRotatePlaying ? 'mdi-pause' : 'mdi-play' }}
                  </v-icon>
                  <img @click="nextHighlight" style="width: 2vw;" class="cursor-pointer" src="https://firebasestorage.googleapis.com/v0/b/renedian-production.appspot.com/o/images%2Flanding%2FArrow%20-%20Right.svg?alt=media&token=72f2e07d-7406-4d3c-a70b-6e946c2bd1c7" alt="" />
                </div>
              </div>
              <div class="flex-1 my-6 my-md-8 d-flex align-center justify-center pa-6 pa-sm-8 pa-md-12">
                <div v-if="currentHighlightImageSrc" class="photo-stack">
                  <img
                    v-for="(layer, idx) in photoStackLayers"
                    :key="idx"
                    :src="$img(layer.src, 1200)"
                    class="photo-frame"
                    :style="getPhotoLayerStyle(layer)"
                    alt="Highlight image layer"
                    loading="lazy"
                    @click.stop="openHighlightImageModal(layer.src)"
                  />
                </div>
                <div v-else class="grey--text text--darken-1">No image available</div>
              </div>
              <div class="d-flex d-md-none justify-end">
                <v-btn icon @click="toggleAutoRotate" aria-label="Toggle highlight slideshow">
                  <v-icon v-if="isAutoRotatePlaying" color="renedian-dark-red">mdi-pause</v-icon>
                  <v-icon v-else color="renedian-dark-red">mdi-play</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else class="text-center grey--text">No highlights available.</div>
          </div>
        </div>
      </div>

      <!-- Highlight Image Modal -->
      <v-dialog
        v-model="highlightImageDialogOpen"
        width="auto"
        max-width="96vw"
        content-class="highlight-image-dialog"
        @click:outside="closeHighlightImageModal"
      >
        <v-card class="highlight-image-modal" flat tile>
          <v-btn icon class="highlight-image-close" aria-label="Close" @click="closeHighlightImageModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-carousel
            v-if="highlightImageCarouselItems.length"
            v-model="highlightImageCarouselIndex"
            class="highlight-image-carousel"
            hide-delimiters
            :show-arrows="highlightImageCarouselItems.length > 1"
            :cycle="false"
            :continuous="false"
            height="auto"
          >
            <v-carousel-item
              v-for="item in highlightImageCarouselItems"
              :key="item.src"
              class="highlight-image-carousel-item"
            >
              <img
                :src="$img(item.src, 1600)"
                :alt="item.alt"
                class="highlight-image"
              />
            </v-carousel-item>
          </v-carousel>
        </v-card>
      </v-dialog>

      <!-- Itinerary -->
      <div ref="itinerary" id="itinerary" class="d-flex flex-column align-center mt-12">
        <div class="d-flex jost-bold grey--text text--darken-3 text-uppercase text-center font-size-xl">Itinerary</div>

        <div class="map-container width-95-vw mt-3 mt-md-6">
          <img class="map-image pa-0 pa-xs-0 pa-sm-2 pa-md-12" :src="$img(template.content.map, 1200)" alt="Tour map" loading="lazy" />
        </div>

        <!-- Itinerary Navigation-->
        <div v-if="itineraryDays.length || excursionDayCount > 0" class="itinerary-nav width-95-vw mt-8" ref="itineraryNav">
          <div class="d-flex flex-column flex-md-row align-center justify-center mb-8">
            <!-- Excursion Section -->
            <div v-if="excursionDayCount > 0" class="d-flex flex-column align-center align-md-start">
              <div class="barlow-condensed-regular-italic mb-4 font-size-md renedian-on-light--text">
                OPTIONAL PRE-TOUR EXCURSION
              </div>
              <div ref="excursionSlotsRow" class="d-flex flex-row align-center" style="flex-wrap: nowrap; overflow: visible; padding: 4px 0;">
                <div v-for="n in excursionDayCount" :key="'ex-' + (n - 1)" class="day-wrapper mr-4 mr-md-6 mb-4" @click="selectItineraryDay(n - 1)">
                  <div :class="['day-circle','day-excursion', 'd-none', 'd-sm-flex', { 'day-selected': selectedItineraryDayIndex === (n - 1) }]">
                    <span class="day-number">{{ n }}</span>
                  </div>
                  <div :class="['day-circle','day-excursion', 'day-small', 'd-sm-none', { 'day-selected': selectedItineraryDayIndex === (n - 1) }]">
                    <span class="day-number">{{ n }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Regular Tour Section -->
            <div v-if="regularDayCount > 0" class="d-flex flex-column align-center align-md-start">
              <div class="barlow-condensed-regular-italic mb-4 font-size-md tour-regular-tour-label">
                REGULAR TOUR
              </div>
              <!-- Extra Small Viewport Navigation -->
              <div class="d-flex d-sm-none flex-row align-center justify-center width-100">
                <div class="day-wrapper mr-4 mb-4 cursor-pointer" @click="selectItineraryDay(excursionDayCount + (selectedRegularDay - 2))">
                  <div class="day-circle day-ellipsis day-small" :class="mobileCanGoLeft ? '' : 'opacity-50'">
                    <v-icon>mdi-chevron-left</v-icon>
                  </div>
                </div>
                <div v-for="dayNum in mobileRegularNavDays" :key="'mobile-day-' + dayNum" class="day-wrapper mr-4 mb-4" @click="selectItineraryDay(excursionDayCount + (dayNum - 1))">
                  <div :class="['day-circle','day-regular', 'day-small', { 'day-selected': selectedItineraryDayIndex === excursionDayCount + (dayNum - 1) }]">
                    <span class="day-number">{{ dayNum }}</span>
                  </div>
                </div>
                <div class="day-wrapper mr-4 mb-4 cursor-pointer" @click="selectItineraryDay(excursionDayCount + selectedRegularDay)">
                  <div class="day-circle day-ellipsis day-small" :class="mobileCanGoRight ? '' : 'opacity-50'">
                    <v-icon>mdi-chevron-right</v-icon>
                  </div>
                </div>
              </div>
              <!-- Small Viewport Navigation and above -->
              <div ref="itinerarySlotsRow" class="d-none d-sm-flex flex-row align-center" style="flex-wrap: nowrap; overflow: visible; padding: 4px 0;">
                <div v-for="(item, idx) in regularNavSlots" :key="'nav-'+idx">
                  <div v-if="item.type === 'ellipsis'" class="day-wrapper mr-4 mr-md-6 mb-4 cursor-pointer" @click="selectItineraryDay(excursionDayCount + (item.targetDay - 1))">
                    <div :class="['day-circle','day-ellipsis']">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </div>
                  </div>
                  <div v-else class="day-wrapper mr-4 mr-md-6 mb-4" @click="selectItineraryDay(excursionDayCount + (item.day - 1))">
                    <div :class="['day-circle','day-regular', { 'day-selected': selectedItineraryDayIndex === excursionDayCount + (item.day - 1) }]">
                      <span class="day-number">{{ item.day }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Day Details Section -->
          <div class="d-flex flex-column flex-md-row my-md-12">
            <div class="flex-1 pa-10 position-relative" v-if="selectedItineraryDay">
              <img v-if="selectedDayPrimaryImageUrl" class="d-block itinerary-photo width-100 mt-12" :src="$img(selectedDayPrimaryImageUrl, 1200)" :alt="`Day ${selectedItineraryDayNumber} itinerary photo`" loading="lazy" />
              <div class="position-absolute d-flex flex-column align-center" style="top: 0; left: 5px">
                <div class="barlow-condensed-regular font-size-huge" style="color:#686d40">
                  DAY
                </div>
                <div class="itinerary-circle white--text jost-bold font-size-xl" :class="{ 'day-excursion': excursionDayCount > 0 && selectedItineraryDayIndex < excursionDayCount }">
                  {{ selectedItineraryDayNumber }}
                </div>
              </div>
            </div>
            <div class="flex-2" v-if="selectedItineraryDay">
              <div class="d-flex flex-row pa-6 mt-0 mt-md-12">
                <div class="flex-2">
                  <div class="barlow-condensed-regular font-size-xl" style="color:#686d40">
                    {{ selectedItineraryDayName }}
                  </div>
                  <div class="barlow-condensed-bold font-size-sm">
                    <span class="renedian-dark-red--text">Riding distance {{ parseInt(selectedItineraryDay.riding.paved) + parseInt(selectedItineraryDay.riding.gravel) }} km</span>
                    <span v-if="selectedItineraryDay.riding.paved || selectedItineraryDay.riding.gravel" class="pl-2 grey--text text--darken-3">(Paved: {{ parseInt(selectedItineraryDay.riding.paved) }} km / Gravel: {{ parseInt(selectedItineraryDay.riding.gravel) }} km)</span> 
                  </div>
                </div>
              </div>
              <div class="barlow-regular pa-6 pt-0 grey--text text--darken-3 font-size-xs line-height-4">
                {{ selectedItineraryDay.description.general }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Vehicle Details Dialog -->
      <v-dialog v-model="vehicleDialogOpen" :fullscreen="isMobileViewport" :max-width="isMobileViewport ? undefined : 960" scrollable>
        <v-card>
          <v-card-title class="barlow-condensed-regular">
            <v-spacer></v-spacer>
            <v-btn icon color="primary" aria-label="Close vehicle details" @click="vehicleDialogOpen = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="vehicle-dialog-content">
            <v-container class="vehicle-details" fluid>
              <v-row>
                <v-col cols="12" md="5" class="d-flex align-center justify-center">
                  <img :src="$img(getVehicleThumb(selectedVehicle), 800)" alt="Vehicle" class="vehicle-details__image" loading="lazy" />
                </v-col>
                <v-col cols="12" md="7">
                  <div class="barlow-condensed-bold renedian-dark-red--text text-uppercase font-size-lg line-height-4">
                    {{ selectedVehicle?.make }} {{ selectedVehicle?.model }}
                  </div>
                  <div class="barlow-regular grey--text text--darken-3 mt-2 font-size-xs line-height-4">
                    {{ selectedVehicle?.description }}
                  </div>

                  <div class="specs mt-6">
                    <div v-for="spec in selectedVehicleSpecs" :key="spec.label" class="spec-item">
                      <div class="spec-label">{{ spec.label }}</div>
                      <div class="spec-value">{{ spec.value }}</div>
                    </div>
                  </div>

                  <div class="barlow-condensed-regular text-uppercase mt-8" style="color:#686d40;">Options</div>
                  <v-list dense class="options-list">
                    <v-list-item v-for="(opt, i) in selectedVehicleOptions" :key="i">
                      <v-list-item-content>
                        <v-list-item-title class="barlow-regular">{{ opt.name }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="barlow-condensed-bold" style="color:#686d40;">
                        {{ formatCurrency(opt.price, selectedVehicle?.rate?.currency || 'CAD') }}
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Included and Excluded -->
      <div class="d-flex flex-row mt-0 mt-md-12 justify-center">
        <div class="width-95-vw">
          <div class="renedian-light-sand--background border-radius-15 tour-inclusions-panels">
            <div class="tour-inclusion-panel">
              <button
                type="button"
                class="tour-inclusion-header barlow-condensed-regular text-uppercase pa-6 width-100 text-left"
                style="font-size: 2rem;"
                :aria-expanded="openPanels.includes(0) ? 'true' : 'false'"
                aria-controls="tour-included-panel"
                @click="toggleInclusionPanel(0)"
              >
                included with tour
              </button>
              <div v-show="openPanels.includes(0)" id="tour-included-panel" class="tour-inclusion-content">
                <ul class="barlow-regular tour-list pa-6">
                  <li v-for="(item, i) in template.content.included" :key="`included-${i}`" class="pb-1 font-size-xs line-height-4">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
            <div class="tour-inclusion-panel">
              <button
                type="button"
                class="tour-inclusion-header renedian-dark-red--text text-uppercase barlow-condensed-regular pa-6 width-100 text-left"
                style="font-size: 2rem;"
                :aria-expanded="openPanels.includes(1) ? 'true' : 'false'"
                aria-controls="tour-excluded-panel"
                @click="toggleInclusionPanel(1)"
              >
                Not included
              </button>
              <div v-show="openPanels.includes(1)" id="tour-excluded-panel" class="tour-inclusion-content">
                <ul class="barlow-regular tour-list pa-6">
                  <li v-for="(item, i) in template.content.excluded" :key="`excluded-${i}`" class="pb-1 font-size-xs line-height-4">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Prices -->
      <div ref="prices" id="prices" class="d-flex flex-column align-center mt-12">
        <div class="d-flex jost-bold grey--text text--darken-3 text-uppercase text-center font-size-xl pb-2">Prices</div>
        
        <!-- Mobile Tab View: Only show if both years are available -->
        <div v-if="hasBothYears" class="d-flex d-md-none flex-row align-end year-tabs-container width-95-vw">
          <!-- First Year Tab -->
          <div 
            @click="mobileTab = 0" 
            class="flex-1 year-tab year-tab-left renedian-burnt-orange--background"
            :class="{ 'year-tab-active': mobileTab === 0 }"
            :style="{ zIndex: mobileTab === 0 ? 2 : 1 }"
          >
            <div class="barlow-condensed-bold white--text font-size-huge">{{ firstYear.year }}</div>
          </div>
          <!-- Second Year Tab -->
          <div 
            @click="mobileTab = 1" 
            class="flex-1 year-tab year-tab-right renedian-sand-grey--background"
            :class="{ 'year-tab-active': mobileTab === 1 }"
            :style="{ zIndex: mobileTab === 1 ? 2 : 1, marginLeft: '-10px' }"
          >
            <div class="barlow-condensed-bold white--text font-size-huge">{{ secondYear.year }}</div>
          </div>
        </div>

        <!-- Mobile Content: Tabbed view when both years exist -->
        <div v-if="hasBothYears" class="d-flex d-md-none flex-column width-95-vw">
          <div v-if="mobileTab === 0">
            <TourDetailsPrice
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :template="template"
              :tour="firstYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="true"
            />
          </div>
          <div v-if="mobileTab === 1">
            <TourDetailsPrice
              :year="secondYear.year"
              color="renedian-sand-grey"
              :template="template"
              :tour="secondYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="true"
            />
          </div>
        </div>
        
        <!-- Mobile Content: Normal view when only one year exists -->
        <div v-else-if="firstYear.year || secondYear.year" class="d-flex d-md-none flex-column width-95-vw mt-6">
          <div v-if="firstYear.year">
            <TourDetailsPrice
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :template="template"
              :tour="firstYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
          <div v-else-if="secondYear.year">
            <TourDetailsPrice
              :year="secondYear.year"
              color="renedian-sand-grey"
              :template="template"
              :tour="secondYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
        </div>

        <!-- Desktop Content: Show both side-by-side if both exist, or single if only one -->
        <div class="d-none d-md-flex flex-column flex-md-row width-95-vw mt-6">
          <div v-if="firstYear.year" class="flex-1" :class="secondYear.year ? 'mr-md-4' : ''">
            <TourDetailsPrice
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :template="template"
              :tour="firstYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
          <div v-if="secondYear.year" class="flex-1" :class="firstYear.year ? 'ml-md-4' : ''">
            <TourDetailsPrice
              :year="secondYear.year"
              color="renedian-sand-grey"
              :template="template"
              :tour="secondYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
        </div>
      </div>

      <!-- Excursions — pricing only from next available tour per year; never template -->
      <div v-if="hasFirstYearExcursion || hasSecondYearExcursion" ref="excursions" id="excursions" class="d-flex flex-column align-center mt-6">
        <!-- Mobile Tab View: Only show if both years have priced excursions -->
        <div v-if="hasBothYearExcursions" class="d-flex d-md-none flex-row align-end year-tabs-container width-95-vw">
          <!-- First Year Tab -->
          <div 
            @click="mobileTab = 0" 
            class="flex-1 year-tab year-tab-left renedian-burnt-orange--background text-center px-2"
            :class="{ 'year-tab-active': mobileTab === 0 }"
            :style="{ zIndex: mobileTab === 0 ? 2 : 1 }"
          >
            <div class="barlow-condensed-bold white--text font-size-sm line-height-3">{{ firstYear.tour?.excursion?.duration }}-DAY PRE-TRIP EXCURSION OPTION</div>
          </div>
          <!-- Second Year Tab -->
          <div 
            @click="mobileTab = 1" 
            class="flex-1 year-tab year-tab-right renedian-sand-grey--background text-center px-2"
            :class="{ 'year-tab-active': mobileTab === 1 }"
            :style="{ zIndex: mobileTab === 1 ? 2 : 1, marginLeft: '-10px' }"
          >
            <div class="barlow-condensed-bold white--text font-size-sm line-height-3">{{ secondYear.tour?.excursion?.duration }}-DAY PRE-TRIP EXCURSION OPTION</div>
          </div>
        </div>

        <!-- Mobile Content: Tabbed view when both years have excursions -->
        <div v-if="hasBothYearExcursions" class="d-flex d-md-none flex-column width-95-vw">
          <div v-if="mobileTab === 0 && hasFirstYearExcursion">
            <TourDetailsExcursion
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :template="template"
              :tour="firstYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="true"
            />
          </div>
          <div v-if="mobileTab === 1 && hasSecondYearExcursion">
            <TourDetailsExcursion
              :year="secondYear.year"
              color="renedian-sand-grey"
              :template="template"
              :tour="secondYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="true"
            />
          </div>
        </div>
        
        <!-- Mobile Content: single year with a priced excursion -->
        <div v-else class="d-flex d-md-none flex-column width-95-vw mt-6">
          <div v-if="hasFirstYearExcursion">
            <TourDetailsExcursion
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :template="template"
              :tour="firstYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
          <div v-else-if="hasSecondYearExcursion">
            <TourDetailsExcursion
              :year="secondYear.year"
              color="renedian-sand-grey"
              :template="template"
              :tour="secondYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
        </div>

        <!-- Desktop Content: one card per year that has a priced excursion -->
        <div class="d-none d-md-flex flex-column flex-md-row width-95-vw">
          <div v-if="hasFirstYearExcursion" class="flex-1" :class="hasSecondYearExcursion ? 'mr-md-4' : ''">
            <TourDetailsExcursion
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :template="template"
              :tour="firstYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
          <div v-if="hasSecondYearExcursion" class="flex-1" :class="hasFirstYearExcursion ? 'ml-md-4' : ''">
            <TourDetailsExcursion
              :year="secondYear.year"
              color="renedian-sand-grey"
              :template="template"
              :tour="secondYear.tour"
              @vehicle-selected="selectVehicle"
              :tabbed="false"
            />
          </div>
        </div>
      </div>

      <!-- Dates -->
      <div ref="dates" id="dates" class="d-flex flex-column align-center mt-12">
        <div class="d-flex jost-bold grey--text text--darken-3 text-uppercase text-center font-size-xl pb-2">Dates</div>

        <!-- Mobile Tab View: Only show if both years are available -->
        <div v-if="hasBothYears" class="d-flex d-md-none flex-row align-end year-tabs-container width-95-vw">
          <!-- First Year Tab -->
          <div 
            @click="mobileTab = 0" 
            class="flex-1 year-tab year-tab-left renedian-burnt-orange--background"
            :class="{ 'year-tab-active': mobileTab === 0 }"
            :style="{ zIndex: mobileTab === 0 ? 2 : 1 }"
          >
            <div class="barlow-condensed-bold white--text font-size-huge">{{ firstYear.year }}</div>
          </div>
          <!-- Second Year Tab -->
          <div 
            @click="mobileTab = 1" 
            class="flex-1 year-tab year-tab-right renedian-sand-grey--background"
            :class="{ 'year-tab-active': mobileTab === 1 }"
            :style="{ zIndex: mobileTab === 1 ? 2 : 1, marginLeft: '-10px' }"
          >
            <div class="barlow-condensed-bold white--text font-size-huge">{{ secondYear.year }}</div>
          </div>
        </div>

        <!-- Mobile Content: Tabbed view when both years exist -->
        <div v-if="hasBothYears" class="d-flex d-md-none flex-column width-95-vw">
          <div v-if="mobileTab === 0 && firstYear.year">
            <TourDetailsDates
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :year-group="firstYear.group"
              :template="template"
              :is-tour-bookable="isTourBookable"
              :tabbed="true"
            />
          </div>
          <div v-if="mobileTab === 1 && secondYear.year">
            <TourDetailsDates
              :year="secondYear.year"
              color="renedian-sand-grey"
              :year-group="secondYear.group"
              :template="template"
              :is-tour-bookable="isTourBookable"
              :tabbed="true"
            />
          </div>
        </div>
        
        <!-- Mobile Content: Normal view when only one year exists -->
        <div v-else-if="firstYear.year || secondYear.year" class="d-flex d-md-none flex-column width-95-vw mt-6">
          <div v-if="firstYear.year">
            <TourDetailsDates
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :year-group="firstYear.group"
              :template="template"
              :is-tour-bookable="isTourBookable"
              :tabbed="false"
            />
          </div>
          <div v-else-if="secondYear.year">
            <TourDetailsDates
              :year="secondYear.year"
              color="renedian-sand-grey"
              :year-group="secondYear.group"
              :template="template"
              :is-tour-bookable="isTourBookable"
              :tabbed="false"
            />
          </div>
        </div>

        <!-- Desktop Content: Show both side-by-side if both exist, or single if only one -->
        <div class="d-none d-md-flex flex-column flex-md-row width-95-vw">
          <div v-if="firstYear.year" class="flex-1" :class="secondYear.year ? 'mr-md-4' : ''">
            <TourDetailsDates
              :year="firstYear.year"
              color="renedian-burnt-orange"
              :year-group="firstYear.group"
              :template="template"
              :is-tour-bookable="isTourBookable"
              :tabbed="false"
            />
          </div>
          <div v-if="secondYear.year" class="flex-1" :class="firstYear.year ? 'ml-md-4' : ''">
            <TourDetailsDates
              :year="secondYear.year"
              color="renedian-sand-grey"
              :year-group="secondYear.group"
              :template="template"
              :is-tour-bookable="isTourBookable"
              :tabbed="false"
            />
          </div>
        </div>
      </div>

      <!-- Book Now Button -->
      <div class="d-flex flex-column align-center py-12">
        <v-btn class="rounded-4 px-12 py-8" color="#2a261b" @click="go('Booking')" large rounded depressed tile dark>
          <span class="barlow-condensed-bold font-size-30 renedian-orange--text px-2">Book Now!</span>
        </v-btn>
      </div>

      <!-- Rider Stories -->
      <div class="width-95-vw mx-auto overflow-x-hidden">
        <RiderStory :template="template.id" />
      </div>

      <!-- Local Lore -->
      <div class="width-95-vw mx-auto overflow-x-hidden">
        <LocalLore :region="template.location.region.name" />
      </div>

    </div>

    <!-- Footer Offset -->
    <div class="d-flex flex-column renedian-light-sand--background align-center py-12"></div>

  </div>
</template>

<script>
import { metaMixin } from '@/mixins/metaMixin'
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore'
import { tourTemplateDocId, templateDeparturesDocId, normalizeContinentSlug, fetchPublicCache } from '@/constants/publicCache'
import { DateTime } from 'luxon'
import RidingBar from '@/components/RidingBar.vue'
import { emitPrerenderReady } from '@/utils/prerender'
import RippedHeader from '@/components/RippedHeader.vue'
import TourDetailsPrice from '@/components/tour/TourDetailsPrice.vue'
import TourDetailsExcursion from '@/components/tour/TourDetailsExcursion.vue'
import TourDetailsDates from '@/components/tour/TourDetailsDates.vue'
import RiderStory from '@/components/RiderStory.vue'
import LocalLore from '@/components/LocalLore.vue'
import { tourState, isListedPublicly, isTourBookable, publicLabel, canViewTour } from '@/schema/tourState'
import { phaseOf } from '@/schema/tourPhases'

export default {
  name: 'Tour',
  mixins: [metaMixin],
  components: {
    RidingBar,
    RippedHeader,
    TourDetailsPrice,
    TourDetailsExcursion,
    TourDetailsDates,
    RiderStory,
    LocalLore
  },
  data() {
    return {
      loading: true,
      error: null,
      tours: [],
      continent: null,
      slug: null,

      // Dynamic year display - shows first two years with tours from 3-year window
      firstYear: {
        year: null,
        tour: {},
        group: {}
      },
      secondYear: {
        year: null,
        tour: {},
        group: {}
      },
      template: null,
      selectedHighlightIndex: 0,
      imagePreloadCache: {},
      isAutoRotatePlaying: true,
      autoRotateTimerId: null,
      autoRotateIntervalMs: 3000,
      highlightImageDialogOpen: false,
      highlightImageCarouselIndex: 0,
      highlightImageResumeAutoRotate: false,
      selectedItineraryDayIndex: 0,
      maxRegularDaysVisible: 10,
      regularSlotsCount: 14,
      minRegularSlots: 6,
      maxRegularSlots: 20,
      slotSizePx: 80,
      slotGapPx: 24,
      // Vehicles carousel
      vehiclesPageIndex: 0,
      vehiclesPerPage: 4,
      selectedVehicle: null,
      vehicleDialogOpen: false,
      vehicleItemWidthPx: 250,
      vehicleItemGapPx: 24,
      vehicleComputedWidthPx: 250,
      viewportWidth: (typeof window !== 'undefined' ? window.innerWidth : 1024),
      _openPanels: [0,1],
      // Mobile Tabs
      mobileTab: 0,
    }
  },
  computed: {
    groupedTours() {
      if (!this.tours.length) return []

      // Group tours by year
      const toursByYear = {}
      
      this.tours.forEach(tour => {
        const startDate = new Date(tour.schedule.primary.start)
        const year = startDate.getFullYear()
        
        if (!toursByYear[year]) {
          toursByYear[year] = []
        }
        
        // Create tour set with slim tour
        const slimTour = {
          schedule: tour.schedule,
          status: tour.status,
          visibility: tour.visibility,
          phase: tour.phase,
          cancelledAt: tour.cancelledAt,
          availability: tour.availability,
          code: tour.reference?.code || tour.id,
          bookings: tour.bookings || []
        }
        
        // Check if excursion exists and has valid start date (not empty string)
        const hasValidExcursion = tour.schedule.excursion && 
          tour.schedule.excursion.start && 
          tour.schedule.excursion.start.trim() !== ''
        
        const tourSet = {
          id: tour.id,
          tour: slimTour,
          mainDates: this.formatDateRange(tour.schedule.primary.start, tour.schedule.primary.finish),
          mainStatus: this.getTourStatus(slimTour),
          hasExcursion: hasValidExcursion,
          excursionDates: hasValidExcursion
            ? this.formatDateRange(tour.schedule.excursion.start, tour.schedule.excursion.finish)
            : null,
          excursionStatus: hasValidExcursion
            ? this.getTourStatus(slimTour, 'excursion')
            : null
        }
        
        toursByYear[year].push(tourSet)
      })
      
      // Convert to array and sort
      return Object.keys(toursByYear)
        .sort()
        .map(year => {
          const yearTours = toursByYear[year].sort((a, b) => 
            new Date(a.tour.schedule.primary.start) - new Date(b.tour.schedule.primary.start)
          )
          
          // Get pricing and vehicles from the first full tour (need to find it from this.tours)
          const firstTourId = yearTours[0].id
          const firstFullTour = this.tours.find(t => t.id === firstTourId) || {}
          const pricing = firstFullTour.pricing || {}
          
          let vehicleOptions = []
          if (firstFullTour.vehicles && Array.isArray(firstFullTour.vehicles)) {
            vehicleOptions = [...firstFullTour.vehicles]
              .sort((a, b) => (a?.order || 0) - (b?.order || 0))
              .map(vehicle => ({
                name: vehicle.name || vehicle.model || 'Unknown Vehicle',
                price: vehicle.price || vehicle.upgrade || 0
              }))
          }
          
          return {
            year: parseInt(year),
            tourSets: yearTours,
            pricing: {
              tour: {
                base: pricing.tour?.base || 0,
                solo: pricing.tour?.solo || 0,
                passenger: pricing.tour?.passenger || 0
              },
              currency: pricing.currency || 'CAD'
            },
            vehicleOptions: vehicleOptions
          }
        })
    },
    highlights() {
      return this.template?.content?.highlights || []
    },
    hasHighlights() {
      return this.highlights.length > 0
    },
    currentHighlight() {
      return this.hasHighlights ? this.highlights[this.selectedHighlightIndex] : {}
    },
    selectedHighlightId: {
      get() {
        return this.currentHighlight?.id
      },
      set(newId) {
        const index = this.highlights.findIndex(h => h.id === newId)
        if (index !== -1) {
          this.selectedHighlightIndex = index
        }
      }
    },
    currentHighlightImageSrc() {
      return this.getImageSrcFromHighlight(this.currentHighlight)
    },
    photoStackLayers() {
      if (!this.currentHighlightImageSrc) return []
      const total = this.highlights.length
      if (!total) return []
      const layerCount = Math.min(3, total)
      const seedKey = String(this.currentHighlight?.id || this.currentHighlight?.title || this.selectedHighlightIndex)
      const rand = this.seedFromString(seedKey)

      const layers = []
      // Build from bottom to top: older images at bottom, current on top
      for (let layerIndex = 0; layerIndex < layerCount; layerIndex++) {
        const offset = layerCount - 1 - layerIndex // bottom uses largest offset
        const srcIndex = (this.selectedHighlightIndex - offset + total) % total
        const highlightForLayer = this.highlights[srcIndex]
        const src = this.getImageSrcFromHighlight(highlightForLayer)
        const rotate = this.randomBetween(rand, -8, 8)
        const translateX = this.randomBetween(rand, -24, 24)
        const translateY = this.randomBetween(rand, -24, 24)
        const baseScale = this.randomBetween(rand, 0.96, 1.03)
        const depthScale = 0.98 + (layerIndex * 0.01) // slightly larger on top
        const scale = baseScale * depthScale
        const opacity = 0.9 + (layerIndex * 0.03)
        layers.push({ src, rotate, translateX, translateY, scale, opacity, zIndex: layerIndex + 1 })
      }
      return layers
    },
    highlightImageCarouselItems() {
      const items = []
      const seen = new Set()
      ;(this.highlights || []).forEach(h => {
        const src = this.getImageSrcFromHighlight(h)
        if (!src || seen.has(src)) return
        seen.add(src)
        items.push({
          src,
          alt: h?.title || this.template?.name || 'Highlight image'
        })
      })
      return items
    },
    hasFirstYearPrice() {
      return Boolean(
        this.firstYear.tour &&
        this.firstYear.tour.pricing &&
        this.firstYear.tour.pricing.tour &&
        this.firstYear.tour.pricing.tour.base !== undefined &&
        this.firstYear.tour.pricing.tour.base !== null
      )
    },
    hasSecondYearPrice() {
      return Boolean(
        this.secondYear.tour &&
        this.secondYear.tour.pricing &&
        this.secondYear.tour.pricing.tour &&
        this.secondYear.tour.pricing.tour.base !== undefined &&
        this.secondYear.tour.pricing.tour.base !== null
      )
    },
    firstYearBasePrice() {
      return this.hasFirstYearPrice ? this.firstYear.tour.pricing.tour.base : null
    },
    secondYearBasePrice() {
      return this.hasSecondYearPrice ? this.secondYear.tour.pricing.tour.base : null
    },
    hasFirstYearExcursion() {
      return Boolean(this.firstYear.year && this.tourHasPricedExcursion(this.firstYear.tour))
    },
    hasSecondYearExcursion() {
      return Boolean(this.secondYear.year && this.tourHasPricedExcursion(this.secondYear.tour))
    },
    hasBothYearExcursions() {
      return this.hasFirstYearExcursion && this.hasSecondYearExcursion
    },
    itineraryDays() {
      const arr = this.template?.itinerary || []
      return Array.isArray(arr) ? arr : []
    },
    excursionItinerary() {
      // Itinerary content from the year’s next tour; no template pricing involvement
      const tour = this.firstYear.tour || this.secondYear.tour || {}
      const itinerary = tour.excursion?.itinerary || this.template?.excursion?.itinerary || []
      return Array.isArray(itinerary) ? itinerary : []
    },
    excursionDayCount() {
      const tour = this.firstYear.tour || this.secondYear.tour || {}
      const tourDuration = tour.excursion?.duration || tour.excursion?.general?.duration
      if (tourDuration && tourDuration > 0) return tourDuration
      const templateDuration = this.template?.excursion?.general?.duration || this.template?.excursion?.duration
      if (templateDuration && templateDuration > 0) return templateDuration
      return this.excursionItinerary.length
    },
    regularDayCount() {
      // Regular days are just the itinerary days (excursion is separate)
      return this.itineraryDays.length
    },
    selectedRegularDay() {
      // 1-indexed within regular days; if selected is excursion, bias to day 1
      return this.selectedItineraryDayIndex >= this.excursionDayCount
        ? (this.selectedItineraryDayIndex - this.excursionDayCount + 1)
        : 1
    },
    selectedItineraryDay() {
      // If selected index is within excursion days, return excursion day
      if (this.selectedItineraryDayIndex < this.excursionDayCount) {
        return this.excursionItinerary[this.selectedItineraryDayIndex] || null
      }
      // Otherwise return regular itinerary day
      const regularIndex = this.selectedItineraryDayIndex - this.excursionDayCount
      return this.itineraryDays[regularIndex] || null
    },
    selectedItineraryDayName() {
      // Excursion days use 'title', regular days use 'name'
      const day = this.selectedItineraryDay
      if (!day) return ''
      return day.title || day.name || ''
    },
    selectedItineraryDayNumber() {
      // If it's an excursion day, return excursion day number (1-indexed)
      if (this.selectedItineraryDayIndex < this.excursionDayCount) {
        return this.selectedItineraryDayIndex + 1
      }
      // If it's a regular tour day, return regular tour day number (1-indexed, starting from 1)
      return this.selectedItineraryDayIndex - this.excursionDayCount + 1
    },
    selectedDayPrimaryImageUrl() {
      const day = this.selectedItineraryDay
      if (!day) return null
      const img = Array.isArray(day?.images) ? day.images[0] : null
      if (!img) return null
      if (typeof img === 'string') return img
      if (typeof img === 'object') return img.url || img.src || null
      return null
    },
    regularNavSlots() {
      const total = this.regularDayCount
      if (total <= 0) return []

      // Use the calculated slots count (already optimized for available space)
      const slots = Math.max(4, this.regularSlotsCount) // must allow first+last + at least 2 middle
      const first = 1
      const last = total

      // If fits within slots, show all days (no ellipses)
      if (total <= slots) {
        return Array.from({ length: total }, (_, i) => ({ type: 'day', day: i + 1 }))
      }

      const remaining = slots - 2 // space for middle between first and last

      // Start with an optimistic window size assuming no ellipses
      let windowLen = remaining
      // Center window around selected regular day
      const center = this.selectedRegularDay
      let start = center - Math.floor((windowLen - 1) / 2)
      let end = start + windowLen - 1
      // Clamp to [2, last-1]
      start = Math.max(2, Math.min(start, last - 1 - windowLen + 1))
      end = start + windowLen - 1

      // Determine ellipses need
      let leftEllipsis = start > 2
      let rightEllipsis = end < last - 1
      let ellipses = (leftEllipsis ? 1 : 0) + (rightEllipsis ? 1 : 0)

      // If ellipses consume slots, shrink window and recenter
      if (ellipses > 0) {
        windowLen = remaining - ellipses
        start = center - Math.floor((windowLen - 1) / 2)
        start = Math.max(2, Math.min(start, last - 1 - windowLen + 1))
        end = start + windowLen - 1
        leftEllipsis = start > 2
        rightEllipsis = end < last - 1
      }

      const result = []
      // First
      result.push({ type: 'day', day: first })
      if (leftEllipsis) result.push({ type: 'ellipsis', targetDay: start - 1 }) // Navigate to day before first visible
      for (let d = start; d <= end; d++) result.push({ type: 'day', day: d })
      if (rightEllipsis) result.push({ type: 'ellipsis', targetDay: end + 1 }) // Navigate to day after last visible
      // Last
      result.push({ type: 'day', day: last })

      // Ensure exactly 'slots' items
      if (result.length > slots) {
        // Trim from middle window edges
        while (result.length > slots && start <= end) {
          if (result[result.length - 2]?.type === 'day' && rightEllipsis) {
            result.splice(result.length - 2, 1)
            end -= 1
          } else if (result[1]?.type === 'day' && leftEllipsis) {
            result.splice(1, 1)
            start += 1
          } else {
            result.splice(Math.ceil(result.length / 2), 1)
          }
        }
      }
      if (result.length < slots) {
        // Pad by extending window if possible, otherwise convert an ellipsis to a day next to window
        while (result.length < slots) {
          if (leftEllipsis && start > 2) {
            start -= 1
            result.splice(1, 0, { type: 'day', day: start })
          } else if (rightEllipsis && end < last - 1) {
            end += 1
            result.splice(result.length - 1, 0, { type: 'day', day: end })
          } else {
            break
          }
        }
      }

      // Recalculate targetDay for ellipses based on final result
      result.forEach((item, idx) => {
        if (item.type === 'ellipsis') {
          if (idx === 1) {
            // Left ellipsis (after day 1) - navigate to day before first visible middle day
            const nextDayItem = result.find((r, i) => i > idx && r.type === 'day')
            if (nextDayItem) {
              item.targetDay = Math.max(2, nextDayItem.day - 1) // Ensure it's at least day 2
            }
          } else {
            // Right ellipsis (before last day) - navigate to day after last visible middle day
            // Find the last day item before this ellipsis
            for (let i = idx - 1; i >= 0; i--) {
              if (result[i].type === 'day' && result[i].day < last) {
                item.targetDay = Math.min(last - 1, result[i].day + 1) // Ensure it's at most last-1
                break
              }
            }
          }
        }
      })

      return result
    },
    mobileRegularNavDays() {
      // For mobile: show 3 day numbers (plus 2 chevrons = 5 circles total)
      // Format: < 1* 2 3 > where * indicates selected
      const total = this.regularDayCount
      if (total <= 0) return []
      
      const selected = this.selectedRegularDay
      const days = []
      
      // Show 3 days centered around selected when possible
      if (total === 1) {
        days.push(1)
      } else if (total === 2) {
        days.push(1, 2)
      } else if (selected === 1) {
        // First day selected: show [1, 2, 3]
        days.push(1, 2, 3)
      } else if (selected === total) {
        // Last day selected: show [last-2, last-1, last]
        days.push(total - 2, total - 1, total)
      } else {
        // Middle day selected: show [selected-1, selected, selected+1]
        days.push(selected - 1, selected, selected + 1)
      }
      
      return days
    },
    mobileCanGoLeft() {
      return this.selectedRegularDay > 1
    },
    mobileCanGoRight() {
      return this.selectedRegularDay < this.regularDayCount
    },
    vehiclesList() {
      const list = this.firstYear.tour?.vehicles || this.template?.vehicles || []
      const arr = Array.isArray(list) ? list : []
      return [...arr].sort((a, b) => (a?.order || 0) - (b?.order || 0))
    },
    vehiclesPageCount() {
      if (!this.vehiclesList.length) return 0
      return Math.ceil(this.vehiclesList.length / this.vehiclesPerPage)
    },
    currentVehiclesSlice() {
      if (!this.vehiclesList.length) return []
      const start = this.vehiclesPageIndex * this.vehiclesPerPage
      return this.vehiclesList.slice(start, start + this.vehiclesPerPage)
    },
    selectedVehicleJson() {
      try {
        return this.selectedVehicle ? JSON.stringify(this.selectedVehicle, null, 2) : 'No vehicle selected'
      } catch (e) {
        return String(this.selectedVehicle)
      }
    },
    selectedVehicleOptions() {
      const opts = this.selectedVehicle?.options || []
      if (!Array.isArray(opts)) return []
      return [...opts].sort((a,b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      })
    },
    selectedVehicleSpecs() {
      const v = this.selectedVehicle || {}
      const specs = []
      // Weight
      if (v.weight?.kilograms || v.weight?.pounds) {
        const kg = v.weight?.kilograms ? `${v.weight.kilograms} kg` : null
        const lb = v.weight?.pounds ? `${v.weight.pounds} lb` : null
        specs.push({ label: 'Weight', value: [kg, lb].filter(Boolean).join(' / ') })
      }
      // Fuel capacity
      if (v.fuel?.capacity) {
        const cap = v.fuel.capacity + (v.fuel.units === 'Metric' ? ' L' : '')
        specs.push({ label: 'Fuel Capacity', value: cap })
      }
      // Range
      if (v.fuel?.range) {
        specs.push({ label: 'Range', value: v.fuel.range + (v.fuel.units == "Metric" ? " km" : " mi") })
      }
      // Seat height
      if (v.height?.range?.low || v.height?.range?.high) {
        const low = v.height?.range?.low || ''
        const high = v.height?.range?.high ? ` - ${v.height.range.high}` : ''
        specs.push({ label: 'Seat Height', value: `${low}${high}`.trim() })
      }
      // Supplement
      if (v.rate?.supplement) {
        specs.push({ label: 'Supplement', value: this.formatCurrency(v.rate.supplement, v.rate.currency || 'CAD') })
      }
      return specs
    },
    firstRiderStory() {
      const stories = this.template?.experiences?.stories || []
      return stories.length > 0 ? stories[0] : null
    },
    firstLocalLore() {
      const lore = this.template?.experiences?.lore || []
      return lore.length > 0 ? lore[0] : null
    },
    isMobileViewport() {
      return this.viewportWidth < 600
    },
    hasBothYears() {
      return !!(this.firstYear.year && this.secondYear.year)
    },
    // Interim: template.type === 'private'. Future: experience.visibility === 'hidden'.
    isHiddenExperience() {
      return (this.template?.type || 'open') === 'private'
    },
    openPanels: {
      get() {
        return this._openPanels
      },
      set(value) {
        this._openPanels = value
      }
    },
  },
  mounted() {
    this.extractRouteParams()
    this.loadTourData()
    this.ensureAutoRotateIfNeeded()
    // Initialize panels based on viewport: closed on xs/sm, open on md+
    this._openPanels = this.viewportWidth >= 960 ? [0, 1] : []
    this.$nextTick(() => {
      this.updateRegularSlotsCount()
      window.addEventListener('resize', this.onWindowResize, { passive: true })
      this.preloadItineraryImages()
      this.computeVehiclesPerPage()
      this.preloadVehicleImages()
    })
  },
  watch: {
    vehiclesList: {
      handler() {
        this.$nextTick(this.preloadVehicleImages)
      },
      deep: true
    },
    $router(newRoute, oldRoute) {
      console.log('Route changed')
      this.extractRouteParams()
      this.loadTourData()
    },
    template() {
      this.selectedHighlightIndex = 0
      this.$nextTick(this.preloadHighlightImages)
      this.ensureAutoRotateIfNeeded()
      // Default to day 1 of regular tour (skip excursion days if they exist)
      this.$nextTick(() => {
        this.selectedItineraryDayIndex = this.excursionDayCount
      })
      this.$nextTick(this.updateRegularSlotsCount)
      this.$nextTick(this.preloadItineraryImages)
      this.$nextTick(() => {
        this.computeVehiclesPerPage()
        this.preloadVehicleImages()
      })
    },
    highlights: {
      handler() {
        this.$nextTick(this.preloadHighlightImages)
        this.ensureAutoRotateIfNeeded()
      },
      deep: true
    },
    itineraryDays: {
      handler() {
        this.$nextTick(this.preloadItineraryImages)
      },
      deep: true
    },
    'firstYear.tour': {
      handler() {
        // Default to day 1 of regular tour when tour data is loaded
        this.$nextTick(() => {
          // Set to first regular tour day (skip excursion days)
          this.selectedItineraryDayIndex = this.excursionDayCount
          // Recalculate slots after tour data loads
          this.updateRegularSlotsCount()
        })
      },
      immediate: true
    },
    regularNavSlots: {
      handler(newVal, oldVal) {
        // Only recalculate if slots actually changed (avoid infinite loops)
        // Skip if this is the initial calculation
        if (oldVal && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.$nextTick(() => {
            this.updateRegularSlotsCount()
          })
        }
      }
    },
    viewportWidth(newWidth) {
      // Close panels on xs/sm (< 960px), open on md+ (>= 960px)
      this._openPanels = newWidth >= 960 ? [0, 1] : []
    },
    highlightImageDialogOpen(isOpen) {
      if (!isOpen) this.handleHighlightImageDialogClosed()
    }
  },
  beforeDestroy() {
    this.stopAutoRotate()
    window.removeEventListener('resize', this.onWindowResize)
  },
  created() {
    console.log('Tour created')
  },
  methods: {
    /** Tour has an available excursion with a numeric base price set on the tour (no template). */
    tourHasPricedExcursion(tour) {
      if (!tour?.excursion?.available) return false
      const base = tour.excursion?.pricing?.base
      if (base === undefined || base === null) return false
      const n = typeof base === 'number' ? base : parseFloat(String(base).replace(/,/g, ''))
      return Number.isFinite(n)
    },
    toggleInclusionPanel(index) {
      if (this.openPanels.includes(index)) {
        this.openPanels = this.openPanels.filter((panelIndex) => panelIndex !== index)
      } else {
        this.openPanels = [...this.openPanels, index]
      }
    },
    extractRouteParams() {
      // Extract continent and slug from route params (preferred) or path (fallback)
      if (this.$route.params.continent && this.$route.params.slug) {
        this.continent = this.$route.params.continent
        this.slug = this.$route.params.slug
      } else {
        // Fallback: Extract from path
        const pathParts = this.$route.path.split('/')
        if (pathParts.length >= 4 && pathParts[1] === 'adventure-motorcycle-tours') {
          this.continent = pathParts[2]
          this.slug = pathParts[3]
        }
      }
      console.log('Extracted route params:', { continent: this.continent, slug: this.slug, routeParams: this.$route.params, routePath: this.$route.path })
    },

    async loadTourData() {
      if (!this.continent || !this.slug) {
        this.error = 'Invalid tour route'
        this.loading = false
        return
      }

      try {
        this.loading = true
        this.error = null

        // Load template based on continent and slug
        await this.loadTemplate()
        
        // If loadTemplate set an error, stop here (it already set loading = false)
        if (this.error) {
          return
        }
        
        if (this.template) {
          // Load tours for this template
          await this.loadTours()
          this.updateMetadata()
        }
      } catch (error) {
        console.error('Error loading tour data:', error)
        this.error = error.message || 'Failed to load tour information'
      } finally {
        this.loading = false
        this.$nextTick(() => emitPrerenderReady())
      }
    },

    async loadTemplate() {
      const db = getFirestore()
      try {
        const cached = await fetchPublicCache(tourTemplateDocId(this.continent, this.slug))
        if (cached?.data) {
          this.template = cached.data
          return
        }
      } catch (e) {
        // fall through to live query
      }

      // ↩️ Fallback: query by slug (avoids full collection scan)
      const templatesRef = collection(db, 'templates')
      const slugQuery = query(templatesRef, where('reference.slug', '==', this.slug))
      const querySnapshot = await getDocs(slugQuery)

      let foundTemplate = null
      querySnapshot.forEach((docSnap) => {
        const template = docSnap.data()
        template.id = docSnap.id
        const templateContinent = normalizeContinentSlug(template.location?.continent)
        if (templateContinent === this.continent) {
          foundTemplate = template
        }
      })

      if (!foundTemplate) {
        console.error('Template not found:', { continent: this.continent, slug: this.slug })
        this.error = `Tour template not found for continent: ${this.continent}, slug: ${this.slug}`
        this.loading = false
        return
      }

      this.template = foundTemplate
    },

    async loadTours() {
      if (this.isHiddenExperience) {
        this.tours = await this.loadToursFromFirestore()
        this.setYearTours()
        return
      }

      const cachedTours = await this.loadToursFromCache()
      if (cachedTours) {
        this.tours = cachedTours
        this.setYearTours()
        return
      }

      this.tours = this.publicDeparturesOnly(await this.loadToursFromFirestore())
      this.setYearTours()
    },

    // What this page may show: every public departure on a public Experience; on a
    // hidden Experience, reached by its link, the hidden ones too (schema/tourState).
    publicDeparturesOnly(tours) {
      return (tours || []).filter((tour) => (
        this.isHiddenExperience ? canViewTour(tour) && !tourState(tour).cancelled : isListedPublicly(tour)
      ))
    },

    async loadToursFromCache() {
      try {
        const cached = await fetchPublicCache(templateDeparturesDocId(this.template.id))
        const years = cached?.data?.years || {}

        const tours = []
        Object.keys(years).forEach((yearKey) => {
          const yearGroup = years[yearKey] || {}
          const pricing = yearGroup.pricing || {}
          const vehicles = Array.isArray(yearGroup.vehicles) ? yearGroup.vehicles : []
          const departures = Array.isArray(yearGroup.departures) ? yearGroup.departures : []

          departures.forEach((dep) => {
            if (!dep?.id || !dep?.schedule?.primary?.start) return
            if (!isListedPublicly(dep)) return
            tours.push({
              id: dep.id,
              schedule: dep.schedule,
              status: dep.status,
              visibility: dep.visibility,
              phase: dep.phase,
              cancelledAt: dep.cancelledAt,
              availability: dep.availability,
              reference: dep.reference || null,
              bookings: dep.bookings || [],
              pricing,
              vehicles,
              excursion: dep.excursion || {}
            })
          })
        })

        const publicTours = this.publicDeparturesOnly(tours)
        if (publicTours.length === 0) return null

        return publicTours.sort((a, b) =>
          new Date(a.schedule.primary.start) - new Date(b.schedule.primary.start)
        )
      } catch (e) {
        return null
      }
    },

    async loadToursFromFirestore() {
      const db = getFirestore()
      const toursRef = collection(db, 'tours')
      const q = query(toursRef, where('template.id', '==', this.template.id))
      const querySnapshot = await getDocs(q)

      const tours = []
      querySnapshot.forEach((docSnap) => {
        const tour = docSnap.data()
        tour.id = docSnap.id

        const startDate = new Date(tour.schedule.primary.start)
        const now = new Date()
        const currentYear = now.getFullYear()
        const tourYear = startDate.getFullYear()
        if (tourYear >= currentYear && !tourState(tour).cancelled && phaseOf(tour) !== 'archived') {
          tours.push(tour)
        }
      })

      return tours.sort((a, b) =>
        new Date(a.schedule.primary.start) - new Date(b.schedule.primary.start)
      )
    },

    setYearTours() {
      const now = new Date()
      // Check a 3-year window for tours
      const yearsToCheck = [
        now.getFullYear(),
        now.getFullYear() + 1,
        now.getFullYear() + 2
      ]

      // Group tours by year
      const toursByYear = {}
      
      this.tours.forEach(tour => {
        const startDate = new Date(tour.schedule.primary.start)
        const year = startDate.getFullYear()
        
        if (!toursByYear[year]) {
          toursByYear[year] = []
        }
        toursByYear[year].push(tour)
      })

      // Sort tours within each year by start date
      Object.keys(toursByYear).forEach(year => {
        toursByYear[year].sort((a, b) => 
          new Date(a.schedule.primary.start) - new Date(b.schedule.primary.start)
        )
      })

      // Find the first two years that have tours (from our 3-year window)
      const yearsWithTours = yearsToCheck.filter(y => toursByYear[y]?.length > 0)

      // Next available = on sale (open or full), not cancelled, not yet departed
      const pickYearTour = (yearTours) => {
        const startOfToday = new Date()
        startOfToday.setHours(0, 0, 0, 0)
        const priceable = yearTours.find((tour) => {
          const state = tourState(tour)
          if (state.cancelled || !['open', 'full'].includes(state.status)) return false
          const start = new Date(tour?.schedule?.primary?.start)
          return !isNaN(start.getTime()) && start >= startOfToday
        })
        return priceable || yearTours[0]
      }

      // Populate firstYear
      if (yearsWithTours[0]) {
        const year = yearsWithTours[0]
        const tour = pickYearTour(toursByYear[year])
        // Sort vehicles by order
        if (tour && Array.isArray(tour.vehicles)) {
          tour.vehicles = [...tour.vehicles].sort((a, b) => (a?.order || 0) - (b?.order || 0))
        }
        this.firstYear = {
          year: year,
          tour: tour,
          group: this.groupedTours.find(g => g.year === year) || {}
        }
      } else {
        this.firstYear = { year: null, tour: {}, group: {} }
      }

      // Populate secondYear
      if (yearsWithTours[1]) {
        const year = yearsWithTours[1]
        const tour = pickYearTour(toursByYear[year])
        // Sort vehicles by order
        if (tour && Array.isArray(tour.vehicles)) {
          tour.vehicles = [...tour.vehicles].sort((a, b) => (a?.order || 0) - (b?.order || 0))
        }
        this.secondYear = {
          year: year,
          tour: tour,
          group: this.groupedTours.find(g => g.year === year) || {}
        }
      } else {
        this.secondYear = { year: null, tour: {}, group: {} }
      }

      // Default to day 1 of regular tour (skip excursion days if they exist)
      this.$nextTick(() => {
        this.selectedItineraryDayIndex = this.excursionDayCount
      })
    },

    formatDateRange(startDate, endDate) {
      // Parse dates as local dates (not UTC) to avoid timezone conversion issues
      const start = DateTime.fromISO(startDate, { zone: 'local' })
      const end = DateTime.fromISO(endDate, { zone: 'local' })
      
      // Format as "MMM d" (e.g., "Sep 23") and convert to uppercase
      const formatDate = (date) => {
        return date.toFormat('MMM d').toUpperCase()
      }
      
      return `${formatDate(start)} - ${formatDate(end)}`
    },

    formatPrice(price) {
      return price.toFixed(0)
    },

    // One rule everywhere (schema/tourState): not cancelled, open, phase before Operating.
    isTourBookable(tour) {
      return isTourBookable(tour).ok
    },

    getTourStatus(tour, scheduleType = 'primary') {
      // The label comes from the tour's state and phase (schema/tourState publicLabel).
      // scheduleType is kept for the excursion column, which shares the departure's label.
      return publicLabel(tour)
    },

    getStatusColor(status) {
      switch (status) {
        case 'OPEN': return 'success'
        case 'ALMOST FULL': return 'warning'
        case 'LIMITED AVAILABILITY': return 'warning'
        case 'FULL': return 'error'
        case 'CLOSED': return 'grey'
        case 'PRIVATE TOUR': return 'info'
        case 'IN PROGRESS': return 'primary'
        case 'COMPLETING': return 'warning'
        case 'COMPLETED': return 'success'
        case 'COMPLETE': return 'success'
        case 'ARCHIVED': return 'grey'
        default: return 'grey'
      }
    },

    getStatusTextColor(status) {
      switch (status) {
        case 'OPEN': return 'white'
        case 'ALMOST FULL': return 'black'
        case 'LIMITED AVAILABILITY': return 'black'
        case 'FULL': return 'white'
        case 'CLOSED': return 'white'
        case 'PRIVATE TOUR': return 'white'
        case 'IN PROGRESS': return 'white'
        case 'COMPLETING': return 'black'
        case 'COMPLETED': return 'white'
        case 'COMPLETE': return 'white'
        case 'ARCHIVED': return 'white'
        default: return 'white'
      }
    },

    updateMetadata() {
      if (!this.template) return
      
      const title = this.template.name
      const description = this.template.content?.website?.brief || 
                         this.template.description?.public || 
                         `Join us for the ${this.template.name} motorcycle tour`
      
      this.updatePageMeta(title, description)
    },
    seedFromString(str) {
      let h = 1779033703 ^ str.length
      for (let i = 0; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
        h = (h << 13) | (h >>> 19)
      }
      return function() {
        h = Math.imul(h ^ (h >>> 16), 2246822507)
        h = Math.imul(h ^ (h >>> 13), 3266489909)
        h ^= h >>> 16
        return (h >>> 0) / 4294967296
      }
    },
    randomBetween(rand, min, max) {
      return min + rand() * (max - min)
    },
    getPhotoLayerStyle(layer) {
      return {
        zIndex: layer.zIndex,
        opacity: layer.opacity,
        transform: `translate(-50%, -50%) rotate(${layer.rotate}deg) translate(${layer.translateX}px, ${layer.translateY}px) scale(${layer.scale})`
      }
    },
    openHighlightImageModal(src) {
      if (!src) return
      const items = this.highlightImageCarouselItems
      const index = items.findIndex(i => i.src === src)
      this.highlightImageCarouselIndex = index >= 0 ? index : 0
      this.highlightImageResumeAutoRotate = this.isAutoRotatePlaying
      if (this.isAutoRotatePlaying) {
        this.isAutoRotatePlaying = false
        this.stopAutoRotate()
      }
      this.highlightImageDialogOpen = true
    },
    closeHighlightImageModal() {
      this.highlightImageDialogOpen = false
    },
    handleHighlightImageDialogClosed() {
      const shouldResume = this.highlightImageResumeAutoRotate
      this.highlightImageResumeAutoRotate = false
      this.highlightImageCarouselIndex = 0
      if (shouldResume) {
        this.isAutoRotatePlaying = true
        this.startAutoRotate()
      }
    },
    nextHighlight() {
      if (!this.hasHighlights) return
      this.selectedHighlightIndex = (this.selectedHighlightIndex + 1) % this.highlights.length
      if (this.isAutoRotatePlaying) this.startAutoRotate()
    },
    prevHighlight() {
      if (!this.hasHighlights) return
      this.selectedHighlightIndex = (this.selectedHighlightIndex - 1 + this.highlights.length) % this.highlights.length
      if (this.isAutoRotatePlaying) this.startAutoRotate()
    },
    getImageSrcFromHighlight(highlight) {
      if (!highlight) return null
      const image = highlight.image
      if (!image) return null
      if (typeof image === 'string') return image
      if (typeof image === 'object') return image.url || image.src || null
      return null
    },
    preloadHighlightImages() {
      if (!this.hasHighlights) return
      this.highlights.forEach(h => {
        const src = this.getImageSrcFromHighlight(h)
        if (src) this.preloadImage(src)
      })
    },
    preloadImage(src) {
      if (this.imagePreloadCache[src]) return
      const img = new Image()
      img.src = src
      this.imagePreloadCache[src] = img
    },
    ensureAutoRotateIfNeeded() {
      if (this.isAutoRotatePlaying && !this.autoRotateTimerId && this.highlights.length > 1) {
        this.startAutoRotate()
      }
    },
    startAutoRotate() {
      if (this.autoRotateTimerId) clearInterval(this.autoRotateTimerId)
      if (this.highlights.length <= 1) return
      this.autoRotateTimerId = setInterval(() => {
        this.nextHighlight()
      }, this.autoRotateIntervalMs)
    },
    stopAutoRotate() {
      if (this.autoRotateTimerId) {
        clearInterval(this.autoRotateTimerId)
        this.autoRotateTimerId = null
      }
    },
    toggleAutoRotate() {
      this.isAutoRotatePlaying = !this.isAutoRotatePlaying
      if (this.isAutoRotatePlaying) {
        this.startAutoRotate()
      } else {
        this.stopAutoRotate()
      }
    },
    toggleHighlight() {
      // Alias for existing template usage: toggle play/pause
      this.toggleAutoRotate()
    },
    scrollToSection(sectionId) {
      const el = this.$refs[sectionId] || document.getElementById(sectionId)
      if (!el) return
      const headerOffset = this.getHeaderOffset()
      const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
      // optional: update hash without scrolling the page again
      if (history && history.replaceState) {
        history.replaceState(null, '', `#${sectionId}`)
      }
    },
    getHeaderOffset() {
      // Adjust if your header height differs across breakpoints
      return window.innerWidth < 600 ? 70 : 100
    },
    selectItineraryDay(index) {
      console.log('selectItineraryDay', index)
      // Total days = excursion days + regular days
      const totalDays = this.excursionDayCount + this.itineraryDays.length
      if (index < 0 || index >= totalDays) return
      this.selectedItineraryDayIndex = index
      // Optionally, scroll map or details into view
    },
    onWindowResize() {
      this.updateRegularSlotsCount()
      this.computeVehiclesPerPage()
      this.viewportWidth = window.innerWidth
    },
    updateRegularSlotsCount() {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        try {
          const row = this.$refs.itinerarySlotsRow
          const container = this.$refs.itineraryNav
          if (!container || !row) return
          
          const containerWidth = container.clientWidth || 0
          if (containerWidth <= 0) return

          // Account for excursion section taking up space (they're side by side)
          let availableWidth = containerWidth
          const excursionRow = this.$refs.excursionSlotsRow
          if (excursionRow && this.excursionDayCount > 0) {
            // Get the parent flex container that holds both sections
            const parentFlex = row.parentElement
            if (parentFlex) {
              // Measure excursion section width (including label and padding)
              const excursionSection = excursionRow.closest('.d-flex.flex-column')
              if (excursionSection) {
                const excursionSectionWidth = excursionSection.offsetWidth || 0
                // Subtract excursion section width plus some margin/gap
                availableWidth = containerWidth - excursionSectionWidth - 48 // 48px for gaps/margins
              }
            }
          }

          // Measure actual day circle width (including margins)
          const dayWrapper = row.querySelector('.day-wrapper')
          const ellipsisWrapper = row.querySelector('.day-ellipsis')
          
          if (dayWrapper) {
            const dayWidth = dayWrapper.offsetWidth || this.slotSizePx
            const dayGap = parseInt(window.getComputedStyle(dayWrapper).marginRight) || this.slotGapPx
            const dayFullWidth = dayWidth + dayGap
            
            // Measure ellipsis width if it exists
            const ellipsisWidth = ellipsisWrapper 
              ? (ellipsisWrapper.offsetWidth || dayWidth) + dayGap 
              : dayFullWidth
            
            // Calculate maximum number of day circles that can fit
            const maxDays = Math.floor((availableWidth + dayGap) / dayFullWidth)
            
            // If we can fit all days without ellipsis, use that
            if (maxDays >= this.regularDayCount) {
              this.regularSlotsCount = this.regularDayCount
              return
            }
            
            // Otherwise, calculate optimal slots to maximize visible days
            // Pattern: [first] + [middle days] + [ellipsis] + [middle days] + [last]
            // Or: [first] + [ellipsis] + [middle days] + [ellipsis] + [last]
            
            // Try to fit as many days as possible
            // Reserve: first (1) + last (1) + ellipsis (0-2)
            let reservedSlots = 2 // first + last
            let availableForDays = availableWidth - (2 * dayFullWidth) // space for first and last
            
            // If we need ellipsis, reserve space for them
            const needsEllipsis = maxDays < this.regularDayCount
            if (needsEllipsis) {
              reservedSlots += 2 // 2 ellipsis (left and right)
              availableForDays -= (2 * ellipsisWidth)
            }
            
            // Calculate how many middle days can fit
            const middleDays = Math.max(0, Math.floor(availableForDays / dayFullWidth))
            
            // Total slots = reserved + middle days
            const totalSlots = reservedSlots + middleDays
            
            // Use the maximum we can fit, but ensure we show at least minRegularSlots
            this.regularSlotsCount = Math.max(
              this.minRegularSlots, 
              Math.min(this.maxRegularSlots, Math.min(totalSlots, maxDays))
            )
          } else {
            // Fallback to original calculation
            const slotFullWidth = this.slotSizePx + this.slotGapPx
            if (slotFullWidth > 0) {
              const count = Math.floor((availableWidth + this.slotGapPx) / slotFullWidth)
              this.regularSlotsCount = Math.max(this.minRegularSlots, Math.min(this.maxRegularSlots, count))
            }
          }
        } catch (e) {
          // ignore measure errors
          console.warn('Error updating regular slots count:', e)
        }
      })
    },
    preloadItineraryImages() {
      if (!this.itineraryDays || !this.itineraryDays.length) return
      try {
        this.itineraryDays.forEach(day => {
          const images = Array.isArray(day?.images) ? day.images : []
          images.forEach(img => {
            let src = null
            if (typeof img === 'string') src = img
            else if (img && typeof img === 'object') src = img.url || img.src || null
            if (src) this.preloadImage(src)
          })
        })
      } catch (e) {
        // ignore
      }
    },
    // Vehicles
    computeVehiclesPerPage() {
      // Measure available width from the row container if possible
      const row = this.$refs.vehiclesRow
      const containerWidth = row ? row.clientWidth : (this.$refs.itineraryNav?.clientWidth || window.innerWidth)
      const unit = this.vehicleItemWidthPx + this.vehicleItemGapPx
      let count = unit > 0 ? Math.floor((containerWidth + this.vehicleItemGapPx) / unit) : 1
      if (!isFinite(count) || count <= 0) {
        // Fallback to breakpoints
        const w = window.innerWidth
        count = w < 600 ? 1 : w < 960 ? 2 : 4
      }
      this.vehiclesPerPage = Math.max(1, Math.min(6, count))
      // Compute even width per visible item
      const totalGap = (this.vehiclesPerPage - 1) * this.vehicleItemGapPx
      const available = Math.max(0, containerWidth - totalGap)
      const perWidth = Math.floor(available / this.vehiclesPerPage)
      this.vehicleComputedWidthPx = Math.max(200, perWidth)
      // Clamp page index
      if (this.vehiclesPageIndex >= this.vehiclesPageCount) {
        this.vehiclesPageIndex = Math.max(0, this.vehiclesPageCount - 1)
      }
    },
    nextVehiclePage() {
      if (!this.vehiclesPageCount) return
      this.vehiclesPageIndex = (this.vehiclesPageIndex + 1) % this.vehiclesPageCount
    },
    prevVehiclePage() {
      if (!this.vehiclesPageCount) return
      this.vehiclesPageIndex = (this.vehiclesPageIndex - 1 + this.vehiclesPageCount) % this.vehiclesPageCount
    },
    preloadVehicleImages() {
      try {
        this.vehiclesList.forEach(v => {
          const src = this.getVehicleThumb(v)
          if (src) this.preloadImage(src)
        })
      } catch (e) { /* ignore */ }
    },
    getVehicleThumb(vehicle) {
      if (!vehicle) return ''
      // Prefer explicit thumbnail field
      const tn = vehicle.images?.thumbnail
      if (typeof tn === 'string') return tn
      if (tn && typeof tn === 'object') return tn.url || tn.src || ''
      // Fallbacks
      if (vehicle.image && typeof vehicle.image === 'string') return vehicle.image
      const arr = Array.isArray(vehicle.images) ? vehicle.images : []
      if (arr.length) {
        const first = arr[0]
        if (typeof first === 'string') return first
        if (first && typeof first === 'object') return first.url || first.src || ''
      }
      return ''
    },
    selectVehicle(vehicle) {
      this.selectedVehicle = vehicle
      this.vehicleDialogOpen = true
    },
    formatCurrency(amount, currency = 'CAD') {
      if (amount === undefined || amount === null || isNaN(Number(amount))) return '-'
      try {
        return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(Number(amount))
      } catch (e) {
        return `${currency} ${Number(amount).toLocaleString()}`
      }
    },
    getStoryImage(story) {
      if (!story) return null
      const image = story.image || story.coverImage || story.images
      if (!image) return null
      if (typeof image === 'string') return image
      if (typeof image === 'object') return image.url || image.src || null
      return null
    },
    getLoreImage(lore) {
      if (!lore) return null
      const image = lore.image || lore.coverImage || lore.images
      if (!image) return null
      if (typeof image === 'string') return image
      if (typeof image === 'object') return image.url || image.src || null
      return null
    },
    goToStory(story) {
      const path = story?.path || story?.link
      if (path && typeof path === 'string' && path.startsWith('/')) {
        this.go(path)
      }
    },
    goToLore(lore) {
      const path = lore?.path || lore?.link
      if (path && typeof path === 'string' && path.startsWith('/')) {
        this.go(path)
      }
    },
    goToStoriesPage() {
      // Navigate to rider stories page - adjust path as needed
      this.go('/rider-stories')
    },
    goToLorePage() {
      // Navigate to local lore page - adjust path as needed
      this.go('/resources/local-lore')
    }
  },
  filters: {
    price(price) {
      if (price === undefined || price === null || isNaN(Number(price))) return price
      return '$' + Number(price).toLocaleString()
    },
    formatPrice(price) {
      if (!price && price !== 0) return ''
      return Number(price).toLocaleString()
    },
    justYear(date) {
      return new Date(date).getFullYear()
    }
  }
}
</script>

<style scoped>

.tour-title {
  font-size: 4vw;
  padding-bottom: 5vh;
}

.tour-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.tour-header {
  text-align: center;
}

.tour-description-block {
  /* Clears the badge that overhangs the header */
  padding: clamp(64px, 6vw, 160px) 5vw 0;
}

.dates-section {
  margin-top: 3rem;
}

.year-card {
  border-radius: 20px !important;
  min-width: 200px;
}

.passport-icon {
  height: 70px
}

.rust-bg {
  background-color: #b85450 !important;
}

.teal-bg {
  background-color: #5a7a7a !important;
}

.year-title {
  color: #f5e6d3;
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
  margin: 0;
}

.tour-set-card {
  border-radius: 15px !important;
  min-height: 250px;
}

.tour-section {
  margin-bottom: 1rem;
}

.date-range {
  font-size: 1.25rem;
  margin-top: 0.5rem;
}

.rust-text {
  color: #b85450 !important;
}

.mobile-top-tear {
  height: 120px; 
}

.mobile-bottom-tear {
  height: 125px;
} 


@media (max-width: 600px) {
  .mobile-top-tear {
    height: 67px; 
  }

  .mobile-bottom-tear {
    height: 79px;
  } 
}

@media (max-width: 768px) {
  .tour-page {
    padding: 1rem;
  }
  
  .year-title {
    font-size: 2rem;
  }
  
  .date-range {
    font-size: 1rem;
  }
}
.red {
  border: 1px solid red
}
.yellow {
  border: 1px solid yellow
}
.blue {
  border: 1px solid blue
}

/* Stacked photo presentation */
.photo-stack {
  position: relative;
  width: 100%;
  max-width: 520px;
  aspect-ratio: 4 / 3;
}

.photo-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 14px solid white;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.18), 0 6px 12px rgba(0,0,0,0.12);
  transition: transform 200ms ease;
  cursor: zoom-in;
}

.highlight-image-dialog {
  width: auto !important;
  max-width: 96vw !important;
}

.highlight-image-modal {
  position: relative;
  background: transparent;
  overflow: visible;
  width: auto;
  display: inline-block;
}

.highlight-image-carousel {
  width: auto;
}

.highlight-image-carousel-item {
  height: auto !important;
}

.highlight-image-carousel ::v-deep .v-window__container,
.highlight-image-carousel ::v-deep .v-window-item,
.highlight-image-carousel ::v-deep .v-carousel__item {
  height: auto !important;
}

.highlight-image-carousel ::v-deep .v-carousel__controls {
  display: none !important;
}

.highlight-image {
  display: block;
  width: auto;
  height: auto;
  max-width: 96vw;
  max-height: 90vh;
  border-radius: 10px;
  box-shadow: 0 12px 34px rgba(0,0,0,0.35), 0 8px 18px rgba(0,0,0,0.22);
}

.highlight-image-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  color: white;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
}

.itinerary-photo {
  border: 14px solid white;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.18), 0 6px 12px rgba(0,0,0,0.12);
  rotate: -4deg
}

@media (max-width: 600px) {
  .photo-frame {
    border-width: 10px;
    border-radius: 10px;
  }
}

.map-container {
  border-radius: 18px;
  padding: 12px;
  background-color: white;
  /* soft outer glow via layered shadows */
  box-shadow:
    0 2px 8px rgba(0,0,0,0.06),
    0 8px 24px rgba(0,0,0,0.08),
    0 16px 48px rgba(0,0,0,0.06);
}

.map-image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 12px;
}


.day-wrapper { cursor: pointer; }

.day-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 120ms ease, box-shadow 120ms ease;
  user-select: none;
}

.day-number {
  font-family: 'Jost', sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  color: #ffffff;
}

.day-excursion { background-color: #b35438; }
.day-regular   { background-color: #a8b36a; }

.day-circle:hover { transform: translateY(-2px); }

.day-selected {
  box-shadow: 0 0 0 3px #f3b22d inset, 0 0 0 4px #f3b22d;
  background-color: #686d40;
}
.day-selected.day-excursion {
  background-color: #743523 !important;
}

.day-ellipsis {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  .v-icon {
    font-size: 3rem;
  }
}

.day-small {
  width: 55px;
  height: 55px;
}
.day-small.day-selected {
  box-shadow: 0 0 0 2px #f3b22d inset, 0 0 0 2px #f3b22d;
}

.itinerary-circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 3px #f3b22d inset, 0 0 0 4px #f3b22d;
  background-color: #686d40;
}
.itinerary-circle.day-excursion {
  background-color: #743523 !important;
}

.itinerary-lists .flex-1 { min-width: 0; overflow: visible; }

.tour-list {
  list-style: disc outside;
  padding-left: 1.25rem;
  margin: 0;
  line-height: 1.6;
  overflow: visible;
}

.tour-list li {
  word-break: break-word;
}

@media (max-width: 600px) {
  .tour-list { padding-left: 1.5rem; }
}

.vehicles-container {
  border-radius: 24px;
  padding: 24px;
  border: 2px solid #76675d;
  background-color: white;
}

.vehicles-row {
  gap: 24px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.vehicles-arrow img {
  width: 28px;
}

.vehicle-item {
  flex: 0 0 auto;
}

.vehicle-image {
  width: 100%;
  height: 200px;
  object-fit: contain;
}

@media (max-width: 960px) {
  .vehicle-item { flex-basis: auto; }
}

@media (max-width: 600px) {
  .vehicles-container { padding: 16px; }
  .vehicle-item { flex-basis: auto; }
  .vehicles-arrow img { width: 22px; }
}

.vehicle-details__image {
  width: 100%;
  max-width: 360px;
  height: 260px;
  object-fit: contain;
}

.specs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

.spec-item {
  background: #f5efe3;
  border-radius: 10px;
  padding: 10px 12px;
}

.spec-label {
  font-family: 'Jost', sans-serif;
  font-weight: 600;
  color: #76675d;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.spec-value {
  font-family: 'Barlow', sans-serif;
  color: #333;
}

.options-list {
  max-height: 240px;
  overflow-y: auto;
}
.vehicle-dialog-content {
  max-height: 80vh;
  overflow-y: auto;
}
.current-year-container {
  border: 2px solid #b35438;
  border-radius: 30px;
}
.next-year-container {
  border: 2px solid #5a6660;
  border-radius: 30px;
}

/* Rider Stories & Local Lore Styles */
.rider-story-photo,
.local-lore-photo {
  width: 100%;
  max-width: 400px;
  height: auto;
  border: 14px solid white;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.18), 0 6px 12px rgba(0,0,0,0.12);
  transform: rotate(-4deg);
  object-fit: cover;
}

@media (max-width: 600px) {
  .rider-story-photo,
  .local-lore-photo {
    border-width: 10px;
    border-radius: 10px;
    transform: rotate(-2deg);
  }
}

.local-lore-bg {
  background-color: #9a9d7a !important; /* Muted olive-green */
}

.rider-stories-link,
.local-lore-link {
  border: 2px dashed #b35438;
  border-radius: 8px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.rider-stories-link:hover,
.local-lore-link:hover {
  background-color: rgba(179, 84, 56, 0.1);
}

@media (max-width: 600px) {
  .rider-stories-link,
  .local-lore-link {
    font-size: 0.9rem;
    padding: 12px !important;
  }
}

.tour-menu-button {
  padding: 12px 8px !important;
  min-height: 48px;
}

.tour-regular-tour-label {
  color: #3d5c5c !important;
}

.opacity-50 {
  opacity: 0.5;
}

/* Year Tabs Styling */
.year-tabs-container {
  position: relative;
  z-index: 1;
  height: 75px;
}

.year-tab {
  cursor: pointer;
  transition: all 0.3s ease;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  position: relative;
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Inactive tab baseline size */
  height: 70px;
}

.year-tab-left {
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
}

.year-tab-right {
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
}

.year-tab-active {
  /* Active tab appears "higher" by being taller (top extends upward),
     while bottoms stay aligned → no gap with content below. */
  height: 75px;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
}

.year-tab:not(.year-tab-active) {
  filter: brightness(0.92);
}

.year-tab:hover {
  filter: brightness(1);
}

.tour-inclusion-header {
  background: transparent;
  border: 0;
  cursor: pointer;
}

.tour-inclusion-panel + .tour-inclusion-panel {
  border-top: 1px solid rgba(90, 102, 96, 0.2);
}
</style>